/**
 * İt333 / Cephe 3 — Kalıcı frontend cache invalidation zırhı (2026-06-08).
 *
 * KARARGAH RİSK RAPORU: 'Frontend cache invalidation yok' zafiyeti.
 * Önceden cache_buster query param eklendi (İt330/İt331) ama:
 * - Tarayıcı yine de Cache-Control duyarsız davranabilir
 * - axios default cache-policy belirsiz
 * - Bayat veri React state'inde uzun süre kalabilir
 *
 * İT333 KALICI ZIRH:
 *
 * 1. attachCacheBusters(axiosInstance)
 *    - GET ve HEAD isteklerine global Cache-Control + Pragma header
 *    - URL'e _t=Date.now() query parametresi ekler
 *    - Idempotent (zaten varsa eklemez)
 *
 * 2. clearStaleCaches()
 *    - localStorage'da TTL'li cache anahtarlarını süresi dolanları temizler
 *    - sessionStorage'ı temizler (sayfa açıldığında stale veri yok)
 *
 * 3. forceComponentRefresh(refKey)
 *    - useState ile React component'ında zorla re-render tetikler
 *    - Cart detail/Packing/Picking ekranlarında "Yenile" butonu için
 *
 * 4. busterParams(extra)
 *    - GET URL'ine eklenecek query parametreleri (_t + ek param'lar)
 */

const NO_CACHE_HEADERS = {
  'Cache-Control': 'no-cache, no-store, must-revalidate',
  'Pragma': 'no-cache',
  'Expires': '0',
};

/**
 * Axios instance'a cache invalidation interceptor ekler.
 * Sadece GET/HEAD istekleri için (POST/PUT/DELETE etkilenmez).
 *
 * @param {Object} axiosInstance - axios veya yapılandırılmış instance
 * @param {Object} options - {includeBusterParam: true, paths: ['carts', 'packing']}
 */
function attachCacheBusters(axiosInstance, options) {
  if (!axiosInstance || !axiosInstance.interceptors) {
    return null;
  }
  const opts = options || {};
  const includeBusterParam = opts.includeBusterParam !== false;
  const pathsAllowList = Array.isArray(opts.paths) ? opts.paths : null;

  const interceptorId = axiosInstance.interceptors.request.use(function (config) {
    const method = (config.method || 'get').toLowerCase();
    if (method !== 'get' && method !== 'head') return config;

    // Path filtresi (allowList varsa)
    if (pathsAllowList) {
      const url = config.url || '';
      const matched = pathsAllowList.some(function (p) {
        return url.indexOf(p) !== -1;
      });
      if (!matched) return config;
    }

    // No-cache header'ları ekle (idempotent)
    config.headers = config.headers || {};
    for (const k in NO_CACHE_HEADERS) {
      if (!(k in config.headers)) {
        config.headers[k] = NO_CACHE_HEADERS[k];
      }
    }

    // _t query param (idempotent)
    if (includeBusterParam) {
      config.params = config.params || {};
      if (!('_t' in config.params)) {
        config.params._t = Date.now();
      }
    }
    return config;
  });
  return interceptorId;
}

/**
 * localStorage'da TTL'li cache anahtarlarını temizler.
 * Anahtar paterni: 'cache:<bucket>:<key>' → {value, expiresAt}
 *
 * @returns {number} silinen anahtar sayısı
 */
function clearStaleCaches() {
  if (typeof window === 'undefined' || !window.localStorage) return 0;
  const now = Date.now();
  let removed = 0;
  try {
    for (let i = window.localStorage.length - 1; i >= 0; i--) {
      const k = window.localStorage.key(i);
      if (!k || k.indexOf('cache:') !== 0) continue;
      try {
        const raw = window.localStorage.getItem(k);
        if (!raw) continue;
        const parsed = JSON.parse(raw);
        if (parsed && parsed.expiresAt && parsed.expiresAt < now) {
          window.localStorage.removeItem(k);
          removed += 1;
        }
      } catch (e) {
        // Parse hatası → defansif olarak sil
        window.localStorage.removeItem(k);
        removed += 1;
      }
    }
  } catch (e) {
    // localStorage erişim hatası (private mode vb.)
    return removed;
  }
  return removed;
}

/**
 * sessionStorage'ı tamamen temizler (oturum içi geçici veri).
 *
 * @returns {boolean} başarı
 */
function clearSessionStorage() {
  if (typeof window === 'undefined' || !window.sessionStorage) return false;
  try {
    window.sessionStorage.clear();
    return true;
  } catch (e) {
    return false;
  }
}

/**
 * GET URL'ine eklenecek query parametre nesnesi.
 * Cache buster (_t) + ek parametreler.
 *
 * @param {Object} extra - ek parametreler {key: value}
 */
function busterParams(extra) {
  const out = { _t: Date.now() };
  if (extra && typeof extra === 'object') {
    for (const k in extra) {
      if (Object.prototype.hasOwnProperty.call(extra, k)) {
        out[k] = extra[k];
      }
    }
  }
  return out;
}

/**
 * İt359 (2026-06-08) — Karargah onaylı AUTH CACHE INVALIDATION.
 *
 * Yetki değişimi anında etkin olsun:
 *  - localStorage cache: kullanıcı oturumu bilgileri (auth:*, cache:user-*)
 *  - sessionStorage tamamen temizlenir
 *  - global auth-version localStorage key bump → tüm sekmeler reflektir
 *  - opsiyonel reason audit (developer log/debug)
 *
 * @param {string} reason - audit/debug için açıklama (örn: 'role_changed', 'logout')
 * @returns {number} silinen anahtar sayısı
 */
function clearAuthCache(reason) {
  if (typeof window === 'undefined' || !window.localStorage) return 0;
  let removed = 0;
  try {
    // 1. localStorage: auth:* + cache:user-* prefix'li anahtarlar
    for (let i = window.localStorage.length - 1; i >= 0; i--) {
      const k = window.localStorage.key(i);
      if (!k) continue;
      if (k.indexOf('auth:') === 0
          || k.indexOf('cache:user-') === 0
          || k.indexOf('cache:permission-') === 0
          || k.indexOf('cache:role-') === 0) {
        window.localStorage.removeItem(k);
        removed += 1;
      }
    }
    // 2. sessionStorage tamamen temizle
    if (window.sessionStorage) {
      try { window.sessionStorage.clear(); } catch (e) { /* ignore */ }
    }
    // 3. auth-version bump (storage event ile diğer sekmelere yayılır)
    try {
      const cur = parseInt(window.localStorage.getItem('auth-version') || '0', 10);
      window.localStorage.setItem('auth-version', String(cur + 1));
    } catch (e) { /* ignore */ }
    // 4. Audit kaydı (debug için)
    if (reason) {
      try {
        window.localStorage.setItem(
          'auth-invalidation-last',
          JSON.stringify({ reason, at: Date.now(), removed }),
        );
      } catch (e) { /* ignore */ }
    }
  } catch (e) { /* ignore */ }
  return removed;
}

/**
 * İt359 — Auth version watcher: yetki değişimi başka sekmede yapıldıysa
 * mevcut sekme React state'ini yeniler. App.js root effect'te kullanılır.
 *
 * @param {Function} onChange - cb(newVersion, oldVersion)
 * @returns {Function} cleanup
 */
function watchAuthVersion(onChange) {
  if (typeof window === 'undefined' || !window.addEventListener || typeof onChange !== 'function') {
    return function noop() {};
  }
  const handler = function (e) {
    if (e && e.key === 'auth-version' && e.newValue !== e.oldValue) {
      try { onChange(e.newValue, e.oldValue); } catch (err) { /* ignore */ }
    }
  };
  window.addEventListener('storage', handler);
  return function cleanup() {
    window.removeEventListener('storage', handler);
  };
}

module.exports = {
  attachCacheBusters,
  clearStaleCaches,
  clearSessionStorage,
  busterParams,
  NO_CACHE_HEADERS,
  // İt359
  clearAuthCache,
  watchAuthVersion,
};
